import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Receipt, Calendar, User, CreditCard, Send, Download, CheckCircle, Sparkles, FileText, MessageSquare } from 'lucide-react';
import GlassPanel from '../components/GlassPanel';
import { AIPanel, AIButton, AIExplanationBox, AIBadge } from '../components/AIComponents';
import { EntitySourceLabel, DataSourceBadge } from '../components/DataFreshnessComponents';
import { LineageBanner } from '../components/LineageComponents';

const mockInvoice = {
  id: 1045,
  title: 'Kitchen Refit - Final Payment',
  client: { name: 'Smith Residence', email: 'john.smith@email.com', address: '123 Oak Street, London, SW1A 1AA' },
  status: 'pending',
  date: '2024-01-15',
  dueDate: '2024-01-29',
  source_type: 'manual',
  source_job_id: 'job123',
  source_quote_id: 'quote456',
  items: [
    { description: 'Kitchen Refit - Balance Due', quantity: 1, unitPrice: 4250, total: 4250 }
  ],
  subtotal: 4250,
  vat: 850,
  total: 5100,
  payments: [],
  balance: 5100,
  notes: 'Thank you for your business. Please make payment by the due date.',
  bankDetails: { name: 'TradeLife Ltd', sortCode: '12-34-56', accountNumber: '12345678' }
};

export function InvoiceDetail() {
  const { id } = useParams();

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 'var(--space-xl)', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Main Content */}
      <div className="detail-page" style={{ maxWidth: 'none' }}>
        <Link to="/invoices" className="back-link"><ArrowLeft size={16} />Back to Invoices</Link>

        {/* Lineage Banner */}
        {mockInvoice.source_quote_id && (
          <LineageBanner 
            quoteId={mockInvoice.source_quote_id}
            jobId={mockInvoice.source_job_id}
            invoiceId={id}
            isImmutable={true}
          />
        )}

        <div className="detail-header">
          <div className="detail-title-row">
            <div>
              <h1 className="detail-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                Invoice #{id}
                <AIBadge text="AI" />
              </h1>
              <p className="detail-subtitle">{mockInvoice.title}</p>
              {/* Source Label */}
              <div style={{ marginTop: 'var(--space-xs)' }}>
                <EntitySourceLabel 
                  source={mockInvoice.source_type} 
                  createdAt={mockInvoice.date}
                />
              </div>
            </div>
            <span className={`status-badge ${mockInvoice.status === 'paid' ? 'paid' : mockInvoice.status === 'overdue' ? 'overdue' : 'pending'}`} style={{ padding: '8px 16px', fontSize: '0.75rem' }}>{mockInvoice.status}</span>
          </div>
        </div>

        <div className="detail-grid mb-xl">
          <div className="detail-item">
            <div className="detail-item-label">Invoice Date</div>
            <div className="detail-item-value"><Calendar size={16} style={{ color: 'var(--accent-primary)', marginRight: '8px' }} />{mockInvoice.date}</div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label">Due Date</div>
            <div className="detail-item-value" style={{ color: 'var(--status-warning)' }}>{mockInvoice.dueDate}</div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label">Client</div>
            <div className="detail-item-value"><User size={16} style={{ color: 'var(--accent-primary)', marginRight: '8px' }} />{mockInvoice.client.name}</div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label">Balance Due</div>
            <div className="detail-item-value" style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--status-warning)' }}>£{mockInvoice.balance.toLocaleString()}</div>
          </div>
        </div>

        {/* Line Items */}
        <GlassPanel title="Invoice Items" titleIcon={Receipt} className="mb-xl">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                  <th style={{ textAlign: 'left', padding: 'var(--space-md)', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Description</th>
                  <th style={{ textAlign: 'right', padding: 'var(--space-md)', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Qty</th>
                  <th style={{ textAlign: 'right', padding: 'var(--space-md)', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Price</th>
                  <th style={{ textAlign: 'right', padding: 'var(--space-md)', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {mockInvoice.items.map((item, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                    <td style={{ padding: 'var(--space-md)', color: 'var(--text-primary)' }}>{item.description}</td>
                    <td style={{ padding: 'var(--space-md)', textAlign: 'right', color: 'var(--text-secondary)' }}>{item.quantity}</td>
                    <td style={{ padding: 'var(--space-md)', textAlign: 'right', color: 'var(--text-secondary)' }}>£{item.unitPrice.toLocaleString()}</td>
                    <td style={{ padding: 'var(--space-md)', textAlign: 'right', color: 'var(--text-primary)', fontWeight: '500' }}>£{item.total.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginTop: 'var(--space-lg)', paddingTop: 'var(--space-lg)', borderTop: '1px solid var(--glass-border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '200px', marginBottom: 'var(--space-sm)' }}>
              <span style={{ color: 'var(--text-muted)' }}>Subtotal:</span>
              <span style={{ color: 'var(--text-primary)' }}>£{mockInvoice.subtotal.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '200px', marginBottom: 'var(--space-sm)' }}>
              <span style={{ color: 'var(--text-muted)' }}>VAT (20%):</span>
              <span style={{ color: 'var(--text-primary)' }}>£{mockInvoice.vat.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '200px', paddingTop: 'var(--space-sm)', borderTop: '1px solid var(--glass-border)' }}>
              <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>Total:</span>
              <span style={{ fontWeight: '700', fontSize: '1.25rem', color: 'var(--accent-primary)' }}>£{mockInvoice.total.toLocaleString()}</span>
            </div>
          </div>
        </GlassPanel>

        {/* Payment Placeholder */}
        <GlassPanel title="Payment" titleIcon={CreditCard} className="mb-xl">
          <div style={{ textAlign: 'center', padding: 'var(--space-xl)', color: 'var(--text-muted)' }}>
            <CreditCard size={48} style={{ marginBottom: 'var(--space-md)', opacity: 0.3 }} />
            <p>Payment integration placeholder</p>
            <p style={{ fontSize: '0.8125rem', marginTop: 'var(--space-xs)' }}>Stripe / Payment link will appear here</p>
          </div>
        </GlassPanel>

        {/* Bank Details */}
        <GlassPanel title="Bank Details" className="mb-xl">
          <div className="responsive-grid-3" style={{ gap: 'var(--space-md)' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 'var(--space-xs)' }}>Account Name</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{mockInvoice.bankDetails.name}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 'var(--space-xs)' }}>Sort Code</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{mockInvoice.bankDetails.sortCode}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 'var(--space-xs)' }}>Account Number</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{mockInvoice.bankDetails.accountNumber}</div>
            </div>
          </div>
        </GlassPanel>

        <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
          <button className="btn btn-primary" disabled style={{ flex: 1 }}><Send size={16} />Send to Client</button>
          <button className="btn btn-secondary" disabled style={{ flex: 1 }}><Download size={16} />Download PDF</button>
          <button className="btn btn-secondary" disabled><CheckCircle size={16} />Mark as Paid</button>
        </div>
      </div>

      {/* AI Side Panel */}
      <div className="ai-side-panel">
        <AIPanel 
          title="AI Invoice Actions" 
          description="AI helps create professional invoices and communicate with clients."
        >
          <div className="ai-actions-grid single-col">
            <AIButton icon={FileText} variant="full" tooltip="AI generation will be enabled soon">
              Generate invoice from quote
            </AIButton>
            <AIButton icon={MessageSquare} variant="full" tooltip="AI generation will be enabled soon">
              Rewrite invoice wording
            </AIButton>
            <AIButton icon={Sparkles} variant="full" tooltip="AI generation will be enabled soon">
              Explain invoice to client
            </AIButton>
          </div>
        </AIPanel>

        <AIExplanationBox>
          AI can help explain invoice details to clients in plain language and suggest professional wording.
        </AIExplanationBox>
      </div>
    </div>
  );
}

export default InvoiceDetail;
